import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { ACTION_STATUS } from '~/components/Admin/ResourceTable';
import { pxToRem } from '~/theme/typography';

type TAdminDialog = {
  status: ACTION_STATUS;
};

type TAdminChip = {
  isDefault: boolean;
};

export const admin = {
  Dialog: styled(Dialog)<TAdminDialog>(({ status }) => ({
    '.MuiPaper-root': {
      ...(status === ACTION_STATUS.EDIT && {
        width: '792px',
      }),
      ...(status === ACTION_STATUS.UPLOAD && {
        width: '600px',
      }),
      ...((status === ACTION_STATUS.BATCH_ON ||
        status === ACTION_STATUS.BATCH_OFF) && {
        width: '420px',
      }),
    },
  })),
  DialogTitle: styled(DialogTitle)(() => ({
    padding: '22px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  })),
  DialogContent: styled(DialogContent)(() => ({
    padding: '0px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  })),
  DialogActions: styled(DialogActions)(() => ({
    padding: '16px 24px',
  })),
  ContentContainer: styled(Box)(() => ({
    display: 'flex',
    gap: '16px',
    flexDirection: 'column',
  })),
  ContentTitle: styled(Typography)(({ theme }) => ({
    width: '100%',
    backgroundColor: theme.palette.grey[200],
    fontWeight: '500',
    fontSize: pxToRem(16),
    padding: '4px 16px',
    borderRadius: '4px',
  })),
  ContentSubContainer: styled(Box)(() => ({
    display: 'flex',
    gap: '12px',
    flexDirection: 'column',
  })),
  InfoContainer: styled(Box)(() => ({
    display: 'flex',
    gap: '24px',
  })),
  InfoTitle: styled(Typography)(({ theme }) => ({
    width: '95px',
    display: 'flex',
    justifyContent: 'flex-end',
    fontSize: pxToRem(16),
    fontWeight: '500',
    color: theme.palette.grey[600],
  })),
  InfoContent: styled(Typography)(({ theme }) => ({
    display: 'flex',
    gap: '8px',
    fontSize: pxToRem(16),
    fontWeight: '400',
    color: theme.palette.grey[800],
    maxWidth: 'calc(100% - 144px)',
    wordBreak: 'break-all',
  })),
  FormControlLabel: styled(FormControlLabel)(() => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'start',
    gap: '24px',
  })),
  Autocomplete: styled(Autocomplete)(() => ({
    width: '100%',
  })),
  Chip: styled(Chip)<TAdminChip>(({ theme, isDefault }) => ({
    backgroundColor: isDefault
      ? theme.palette.grey[300]
      : theme.palette.common.white,
    color: isDefault ? theme.palette.common.black : theme.palette.warning.main,
    border: isDefault ? 'none' : `solid 1px ${theme.palette.warning.main}`,
    fontSize: pxToRem(14),
    fontWeight: '400',
    margin: '4px',
    '.MuiSvgIcon-root': {
      fill: theme.palette.warning.main,
      display: isDefault ? 'none' : 'block',
    },
  })),
  TextField: styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.info.main,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.info.main,
      },
    },
  })),
  FileUploadContainer: styled(Button)(() => ({
    color: '#919EAB',
    display: 'flex',
    padding: '24px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    alignSelf: 'stretch',
    borderRadius: '16px',
    border: '2px dashed rgba(145, 158, 171, 0.24)',
    background: '#F9FAFB',
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '22px',
    marginBottom: '16px',
  })),
  FileName: styled('div')(() => ({
    fontSize: '16px',
    lineHeight: '1.5',
    '&::before': {
      content: '"・"',
    },
  })),
  FileInfoContent: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  })),
  FileInfoText: styled('p')(() => ({
    margin: '0',
    flexGrow: '1',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#000',
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '1.5',
  })),
  FileInfoButton: styled(Button)(() => ({
    flexShrink: '0',
  })),
  FileLoadingBar: styled('div')(() => ({
    padding: '4px 16px',
    marginBottom: '16px',
    borderRadius: '16px',
    border: '1px solid  rgba(145, 158, 171, 0.48)',
    background: '#F4F6F8',
  })),
  FileErrorContainer: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
    borderRadius: '16px',
    border: '1px solid rgba(255, 72, 66, 0.48)',
    overflow: 'hidden',
  })),
  FileErrorTitle: styled('div')(() => ({
    display: 'flex',
    padding: '4px 16px',
    alignItems: 'center',
    gap: '4px',
    alignSelf: 'stretch',
    color: '#B72136',
    backgroundColor: '#FFE7D9',
  })),
  FileErrorContent: styled(List)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    color: '#FF4842',
  })),
  FileErrorItem: styled(ListItem)(() => ({
    padding: '0 16px',
  })),
};
